import React, { useState, useEffect } from 'react';
import { ChevronsUp, Copy } from 'lucide-react';
import Spinner from './Spinner';
import ErrorMessage from './ErrorMessage';
import PracticeSettingsService from '../services/PracticeSettingsService';
import { splitReadingStoryLines as splitReadingSentences } from '../utils/readingStory';
import './FlashCardForm.css';

/**
 * FlashCardForm component for creating and editing flash cards
 * 
 * @param {Object} props
 * @param {Object} props.initialData - Optional flash card data for editing
 * @param {string} props.initialData.front - Front side content
 * @param {string} props.initialData.back - Back side content
 * @param {Function} props.onSubmit - Callback with form data { front, back }
 * @param {Function} props.onCancel - Callback to cancel operation
 * @param {Function} props.onDelete - Optional callback to delete the current card when editing
 * @param {Function} props.onLevelUp - Optional callback to level up the current card when editing
 * @param {boolean} props.showSubmitAction - Whether to show the normal create/update submit button
 * @param {boolean} props.showCancelAction - Whether to show the form-level cancel button
 */
function normalizeContent(value) {
  return (value || '').trim();
}

function buildReadingText(initialData) {
  if (!initialData) return '';
  const title = initialData.readingTitle || initialData.front || '';
  if (Array.isArray(initialData.readingSegments) && initialData.readingSegments.length > 0) {
    const names = initialData.readingCharacterNames || {};
    const nameLines = [
      names.A && names.A !== 'A' ? `@A name: ${names.A}` : null,
      names.B && names.B !== 'B' ? `@B name: ${names.B}` : null,
    ].filter(Boolean);
    const segmentLines = initialData.readingSegments.map((segment) => {
      if (segment.type === 'scene') return `@Scene: ${segment.text || ''}`;
      if (segment.type === 'story') return `@Story: ${segment.text || ''}`;
      return `@${segment.speaker === 'B' ? 'B' : 'A'}${segment.practiceMode === 'once' ? '[once]' : ''}: ${segment.text || ''}`;
    });
    return [`@R: ${title}`, ...nameLines, ...segmentLines].filter(Boolean).join('\n');
  }
  const content = initialData.readingContent || initialData.back || '';
  return [`@R: ${title}`, content ? `@Story: ${content}` : null].filter(Boolean).join('\n');
}

function parseReadingTagLine(line) {
  const match = String(line || '').trim().match(/^@([AB]\s+name|[AB](?:\[once\])?|R|Scene|Story)\s*:\s*(.*)$/i);
  if (!match) return null;
  return {
    tag: match[1].replace(/\[once\]/i, '').replace(/\s+/g, ' ').toLowerCase(),
    ...(/\[once\]/i.test(match[1]) ? { practiceMode: 'once' } : {}),
    value: match[2].trim(),
  };
}

function parseReadingText(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const firstTaggedIndex = lines.findIndex((line) => line.trim());
  const firstLine = firstTaggedIndex >= 0 ? lines[firstTaggedIndex].trim() : '';
  const titleMatch = firstLine.match(/^@R\s*:\s*(.*)$/i);
  const title = titleMatch ? titleMatch[1].trim() : '';
  const readingCharacterNames = { A: 'A', B: 'B' };
  const readingSegments = [];
  let activeSegment = null;

  lines.slice(firstTaggedIndex + 1).forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const tagged = parseReadingTagLine(trimmed);
    if (tagged) {
      if (tagged.tag === 'a name' || tagged.tag === 'b name') {
        const speaker = tagged.tag[0].toUpperCase();
        if (tagged.value) readingCharacterNames[speaker] = tagged.value;
        activeSegment = null;
        return;
      }
      if (tagged.tag === 'scene') {
        activeSegment = { type: 'scene', text: tagged.value, practice: false };
        readingSegments.push(activeSegment);
        return;
      }
      if (tagged.tag === 'story') {
        activeSegment = { type: 'story', text: tagged.value, practice: true };
        readingSegments.push(activeSegment);
        return;
      }
      if (tagged.tag === 'a' || tagged.tag === 'b') {
        activeSegment = { type: 'speech', speaker: tagged.tag.toUpperCase(), text: tagged.value, practice: true,
          ...(tagged.practiceMode ? { practiceMode: tagged.practiceMode } : {}) };
        readingSegments.push(activeSegment);
      }
      return;
    }

    if (activeSegment) {
      activeSegment.text = [activeSegment.text, trimmed].filter(Boolean).join('\n');
    }
  });

  const normalizedSegments = readingSegments.filter((segment) => String(segment.text || '').trim());
  const readingSentences = normalizedSegments.flatMap((segment) => {
    if (segment.type === 'story') return splitReadingSentences(segment.text);
    if (segment.type === 'speech') return [segment.text];
    return [];
  });
  const readingContent = normalizedSegments.map((segment) => {
    if (segment.type === 'scene') return `@Scene: ${segment.text}`;
    if (segment.type === 'story') return `@Story: ${segment.text}`;
    return `@${segment.speaker}${segment.practiceMode === 'once' ? '[once]' : ''}: ${segment.text}`;
  }).join('\n');

  return {
    hasTitleTag: Boolean(titleMatch),
    title,
    readingContent,
    readingText: [`@R: ${title}`, readingContent].filter(Boolean).join('\n'),
    readingSegments: normalizedSegments,
    readingSentences,
    readingCharacterNames,
  };
}

function validateLevelUpContent(formData, initialData) {
  if (!formData.front) return 'Front Side cannot be blank.';
  if (!formData.back) return 'Back Side cannot be blank.';
  if (formData.front.length > 500) return 'Front Side cannot exceed 500 characters.';
  if (formData.back.length > 500) return 'Back Side cannot exceed 500 characters.';
  if (
    initialData &&
    normalizeContent(formData.front) === normalizeContent(initialData.front) &&
    normalizeContent(formData.back) === normalizeContent(initialData.back) &&
    normalizeContent(formData.contextPre) === normalizeContent(initialData.contextPre) &&
    normalizeContent(formData.memo) === normalizeContent(initialData.memo) &&
    normalizeContent(formData.contextAfter) === normalizeContent(initialData.contextAfter) &&
    normalizeContent(formData.backMemo) === normalizeContent(initialData.backMemo)
  ) {
    return 'Change Front Side, Back Side, or memo/context before leveling up.';
  }
  return '';
}

function FlashCardForm({
  initialData,
  onSubmit,
  onCancel,
  onDelete,
  onLevelUp,
  showSubmitAction = true,
  showCancelAction = true,
}) {
  const [frontContent, setFrontContent] = useState('');
  const [backContent, setBackContent] = useState('');
  const [ttsText, setTtsText] = useState('');
  const [contextPreContent, setContextPreContent] = useState('');
  const [memoContent, setMemoContent] = useState('');
  const [contextAfterContent, setContextAfterContent] = useState('');
  const [backMemoContent, setBackMemoContent] = useState('');
  const [formCardType, setFormCardType] = useState(PracticeSettingsService.CARD_TYPES.NORMAL);
  const [readingText, setReadingText] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLevelingUp, setIsLevelingUp] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(null);
  const canLevelUp = Boolean(
    initialData &&
    onLevelUp &&
    (initialData.levelUpAvailable || PracticeSettingsService.isCardUpgradeable(initialData))
  );

  const applyMarkdownWrapper = (fieldId, value, setValue, prefix, suffix = prefix) => {
    const textarea = document.getElementById(fieldId);
    if (!textarea) return;

    const selectionStart = textarea.selectionStart ?? value.length;
    const selectionEnd = textarea.selectionEnd ?? value.length;
    const selectedText = value.slice(selectionStart, selectionEnd);
    const wrappedText = `${prefix}${selectedText || 'text'}${suffix}`;
    const nextValue = `${value.slice(0, selectionStart)}${wrappedText}${value.slice(selectionEnd)}`;
    const nextSelectionStart = selectionStart + prefix.length;
    const nextSelectionEnd = nextSelectionStart + (selectedText || 'text').length;

    setValue(nextValue);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    });
  };

  const copyFieldText = async (fieldId, value) => {
    const textarea = document.getElementById(fieldId);
    const hasSelection = textarea && textarea.selectionEnd > textarea.selectionStart;
    const textToCopy = hasSelection
      ? value.slice(textarea.selectionStart, textarea.selectionEnd)
      : value;

    if (!textToCopy) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const copyTarget = document.createElement('textarea');
        copyTarget.value = textToCopy;
        copyTarget.setAttribute('readonly', '');
        copyTarget.style.position = 'fixed';
        copyTarget.style.opacity = '0';
        document.body.appendChild(copyTarget);
        copyTarget.select();
        document.execCommand('copy');
        document.body.removeChild(copyTarget);
      }
      textarea?.focus();
    } catch (error) {
      setSubmitError(new Error('Could not copy text to clipboard.'));
    }
  };

  const renderMarkdownToolbar = (fieldId, value, setValue) => (
    <div className="markup-toolbar" role="toolbar" aria-label="Markup formatting tools">
      <button
        type="button"
        className="markup-toolbar-btn"
        onClick={() => applyMarkdownWrapper(fieldId, value, setValue, '**')}
        disabled={isSubmitting || isLevelingUp}
        title="Bold"
        aria-label="Bold"
      >
        B
      </button>
      <button
        type="button"
        className="markup-toolbar-btn"
        onClick={() => applyMarkdownWrapper(fieldId, value, setValue, '*')}
        disabled={isSubmitting || isLevelingUp}
        title="Italic"
        aria-label="Italic"
      >
        I
      </button>
      <button
        type="button"
        className="markup-toolbar-btn markup-toolbar-icon-btn"
        onClick={() => copyFieldText(fieldId, value)}
        disabled={isSubmitting || isLevelingUp || !value}
        title="Copy"
        aria-label="Copy"
      >
        <Copy size={16} aria-hidden="true" />
      </button>
    </div>
  );

  // Initialize form with initial data if provided (for editing)
  useEffect(() => {
    if (initialData) {
      const initialCardType = PracticeSettingsService.getCardType(initialData);
      const initialReadingText = initialCardType === PracticeSettingsService.CARD_TYPES.READING
        ? buildReadingText(initialData)
        : '';
      setFormCardType(initialCardType);
      setFrontContent(initialData.front || '');
      setBackContent(initialData.back || '');
      setTtsText(initialData.ttsText || '');
      setReadingText(initialReadingText);
      setContextPreContent(initialData.contextPre || '');
      setMemoContent(initialData.memo || '');
      setContextAfterContent(initialData.contextAfter || '');
      setBackMemoContent(initialData.backMemo || '');
      setInitialFormSnapshot({
        formCardType: initialCardType,
        front: normalizeContent(initialData.front),
        back: normalizeContent(initialData.back),
        ttsText: normalizeContent(initialData.ttsText),
        readingText: normalizeContent(initialReadingText),
        contextPre: normalizeContent(initialData.contextPre),
        memo: normalizeContent(initialData.memo),
        contextAfter: normalizeContent(initialData.contextAfter),
        backMemo: normalizeContent(initialData.backMemo),
      });
    } else {
      setInitialFormSnapshot(null);
    }
  }, [initialData]);

  const currentFormSnapshot = {
    formCardType,
    front: normalizeContent(frontContent),
    back: normalizeContent(backContent),
    ttsText: normalizeContent(ttsText),
    readingText: normalizeContent(readingText),
    contextPre: normalizeContent(contextPreContent),
    memo: normalizeContent(memoContent),
    contextAfter: normalizeContent(contextAfterContent),
    backMemo: normalizeContent(backMemoContent),
  };
  const isEditDirty = Boolean(
    initialData &&
    initialFormSnapshot &&
    JSON.stringify(currentFormSnapshot) !== JSON.stringify(initialFormSnapshot)
  );

  /**
   * Validate form content
   * Requirements 1.5: Validate that flash card content is not empty
   * @returns {Object} Validation errors object
   */
  const validateForm = () => {
    const newErrors = {};

    if (formCardType === PracticeSettingsService.CARD_TYPES.READING) {
      const readingData = parseReadingText(readingText);
      if (!readingData.hasTitleTag || !readingData.title) {
        newErrors.reading = 'Reading cards need an @R: title.';
      } else if (!readingData.readingSegments.some((segment) => segment.practice && segment.text)) {
        newErrors.reading = 'Reading cards need @Story:, @A:, or @B: practice content.';
      }
      return newErrors;
    }

    // Validate front content is not empty or whitespace-only
    if (!frontContent || frontContent.trim() === '') {
      newErrors.front = 'Front content is required and cannot be empty';
    }

    // Validate back content is not empty or whitespace-only
    if (!backContent || backContent.trim() === '') {
      newErrors.back = 'Back content is required and cannot be empty';
    }

    return newErrors;
  };

  /**
   * Handle form submission
   * Requirements 1.1: Create new flash card with front and back content
   * Requirements 1.3: Edit existing flash card
   */
  const buildFormData = () => {
    if (formCardType === PracticeSettingsService.CARD_TYPES.READING) {
      const readingData = parseReadingText(readingText);
      return {
        cardType: PracticeSettingsService.CARD_TYPES.READING,
        front: readingData.title,
        back: readingData.readingContent,
        readingTitle: readingData.title,
        readingContent: readingData.readingContent,
        readingText: readingData.readingText,
        readingSegments: readingData.readingSegments,
        readingSentences: readingData.readingSentences,
        readingCharacterNames: readingData.readingCharacterNames,
        memo: null,
        contextPre: null,
        contextAfter: null,
        backMemo: null,
      };
    }

    return {
      cardType: formCardType === PracticeSettingsService.CARD_TYPES.SIMPLE
        ? PracticeSettingsService.CARD_TYPES.SIMPLE
        : PracticeSettingsService.CARD_TYPES.NORMAL,
      front: frontContent.trim(),
      back: backContent.trim(),
      ...((ttsText.trim() || initialData?.ttsText) ? { ttsText: ttsText.trim() || null } : {}),
      memo: memoContent.trim() || null,
      contextPre: contextPreContent.trim() || null,
      contextAfter: contextAfterContent.trim() || null,
      backMemo: backMemoContent.trim() || null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});
    setSubmitError(null);

    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Prevent duplicate submissions
    if (isSubmitting || isLevelingUp || !onSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call onSubmit callback with form data
      const formData = buildFormData();

      await onSubmit(formData);

      // Clear form after successful submission (only for create, not edit)
      if (!initialData) {
        setFrontContent('');
        setBackContent('');
        setTtsText('');
        setReadingText('');
        setContextPreContent('');
        setMemoContent('');
        setContextAfterContent('');
        setBackMemoContent('');
      }
    } catch (error) {
      // Display error from API
      setSubmitError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLevelUp = async () => {
    setErrors({});
    setSubmitError(null);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isSubmitting || isLevelingUp || !onLevelUp) {
      return;
    }

    const formData = buildFormData();
    const levelUpError = validateLevelUpContent(formData, initialData);
    if (levelUpError) {
      setSubmitError(new Error(levelUpError));
      return;
    }

    setIsLevelingUp(true);

    try {
      await onLevelUp(formData);
    } catch (error) {
      setSubmitError(error);
    } finally {
      setIsLevelingUp(false);
    }
  };

  /**
   * Handle cancel action
   */
  const handleCancel = () => {
    // Clear form and errors
    setFrontContent('');
    setBackContent('');
    setReadingText('');
    setContextPreContent('');
    setMemoContent('');
    setContextAfterContent('');
    setBackMemoContent('');
    setErrors({});
    setSubmitError(null);
    
    if (onCancel) {
      onCancel();
    }
  };

  /**
   * Clear error for a specific field when user starts typing
   */
  const handleFrontChange = (e) => {
    setFrontContent(e.target.value);
    if (errors.front) {
      setErrors({ ...errors, front: undefined });
    }
  };

  const handleBackChange = (e) => {
    setBackContent(e.target.value);
    if (errors.back) {
      setErrors({ ...errors, back: undefined });
    }
  };

  return (
    <div className="flashcard-form-container">
      {!initialData && <h2>Create New Flash Card</h2>}
      
      {isSubmitting && (
        <div className="form-loading-overlay">
          <Spinner size="medium" message="Saving flash card..." />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flashcard-form">
        <div className="form-group">
          {formCardType === PracticeSettingsService.CARD_TYPES.READING ? (
            <select
              id="card-type-select"
              className="form-input"
              aria-label="Card type"
              value={formCardType}
              onChange={(e) => setFormCardType(e.target.value)}
              disabled={isSubmitting || isLevelingUp || Boolean(initialData)}
            >
              <option value={PracticeSettingsService.CARD_TYPES.NORMAL}>Q/A Card</option>
              <option value={PracticeSettingsService.CARD_TYPES.READING}>Reading Article</option>
            </select>
          ) : (
            <div className={`card-type-switch ${!initialData ? 'card-type-switch-create' : ''}`.trim()} role="group" aria-label="Card type">
              <button
                type="button"
                className={`card-type-switch-option ${formCardType === PracticeSettingsService.CARD_TYPES.SIMPLE ? 'active' : ''}`.trim()}
                onClick={() => setFormCardType(PracticeSettingsService.CARD_TYPES.SIMPLE)}
                disabled={isSubmitting || isLevelingUp}
                aria-pressed={formCardType === PracticeSettingsService.CARD_TYPES.SIMPLE}
              >
                Short
              </button>
              <button
                type="button"
                className={`card-type-switch-option ${formCardType === PracticeSettingsService.CARD_TYPES.NORMAL ? 'active' : ''}`.trim()}
                onClick={() => setFormCardType(PracticeSettingsService.CARD_TYPES.NORMAL)}
                disabled={isSubmitting || isLevelingUp}
                aria-pressed={formCardType === PracticeSettingsService.CARD_TYPES.NORMAL}
              >
                Sentence
              </button>
              {!initialData && (
                <>
                  <button
                    type="button"
                    className={`card-type-switch-option ${formCardType === PracticeSettingsService.CARD_TYPES.READING ? 'active' : ''}`.trim()}
                    onClick={() => setFormCardType(PracticeSettingsService.CARD_TYPES.READING)}
                    disabled={isSubmitting || isLevelingUp}
                    aria-pressed={formCardType === PracticeSettingsService.CARD_TYPES.READING}
                  >
                    Reading
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {formCardType === PracticeSettingsService.CARD_TYPES.READING ? (
          <div className="form-group">
            <div className="form-label-row">
              <label htmlFor="reading-content">
                Reading Article <span className="required">*</span>
              </label>
              {renderMarkdownToolbar('reading-content', readingText, setReadingText)}
            </div>
            <textarea
              id="reading-content"
              className={`form-textarea flashcard-form-reading-textarea ${errors.reading ? 'error' : ''}`}
              value={readingText}
              onChange={(e) => {
                setReadingText(e.target.value);
                if (errors.reading) setErrors({ ...errors, reading: undefined });
              }}
              placeholder={'@R: Lunch conversation\n@A name: Mina\n@B name: Joon\n@Scene: In the cafe\n@A: Did you order already?\n@B: Not yet. What do you recommend?\n@Story: They looked at the menu for a minute. Mina pointed at the soup.'}
              rows={10}
              disabled={isSubmitting || isLevelingUp}
            />
            {errors.reading && (
              <div className="error-message">{errors.reading}</div>
            )}
          </div>
        ) : (
          <>
        {/* Memo fields */}
        <div className="context-dialog-row context-dialog-row-before">
          <input
            type="text"
            id="context-pre-content"
            className="form-input context-dialog-input"
            aria-label="Context - Before"
            value={contextPreContent}
            onChange={(e) => setContextPreContent(e.target.value)}
            placeholder="Question, situation, or context before the card (optional)"
            disabled={isSubmitting || isLevelingUp}
          />
        </div>

        {/* Front content field */}
        <div className="form-group card-editor-group card-editor-group-front">
          <div className="form-label-row">
            <label htmlFor="front-content">
              Front Side <span className="required">*</span>
            </label>
            {renderMarkdownToolbar('front-content', frontContent, setFrontContent)}
          </div>
          <textarea
            id="front-content"
            className={`form-textarea card-editor-primary ${errors.front ? 'error' : ''}`}
            value={frontContent}
            onChange={handleFrontChange}
            placeholder="Enter the word or sentence to learn"
            rows={4}
            disabled={isSubmitting || isLevelingUp}
          />
          {errors.front && (
            <div className="error-message">{errors.front}</div>
          )}
        </div>

        <div className="form-group card-editor-memo-group card-editor-memo-group-front">
          <input
            type="text"
            id="memo-content"
            aria-label="Front memo"
            className="form-input card-editor-memo card-editor-memo-front"
            value={memoContent}
            onChange={(e) => setMemoContent(e.target.value)}
            placeholder="Hint for guessing the answer (optional)"
            disabled={isSubmitting || isLevelingUp}
          />
        </div>

        {/* Back content field */}
        <div className="form-group card-editor-group card-editor-group-back">
          <div className="form-label-row">
            <label htmlFor="back-content">
              Back Side <span className="required">*</span>
            </label>
            {renderMarkdownToolbar('back-content', backContent, setBackContent)}
          </div>
          <textarea
            id="back-content"
            className={`form-textarea card-editor-primary ${errors.back ? 'error' : ''}`}
            value={backContent}
            onChange={handleBackChange}
            placeholder="Enter the translation or meaning"
            rows={4}
            disabled={isSubmitting || isLevelingUp}
          />
          {errors.back && (
            <div className="error-message">{errors.back}</div>
          )}
        </div>

        <div className="form-group card-editor-memo-group card-editor-memo-group-back">
          <label htmlFor="tts-text">Answer pronunciation (optional)</label>
          <input
            type="text"
            id="tts-text"
            className="form-input card-editor-memo card-editor-memo-back"
            value={ttsText}
            onChange={(event) => setTtsText(event.target.value)}
            placeholder="Leave blank to read Back Side"
            disabled={isSubmitting || isLevelingUp}
          />
        </div>

        <div className="form-group card-editor-memo-group card-editor-memo-group-back">
          <input
            type="text"
            id="back-memo-content"
            aria-label="Back memo"
            className="form-input card-editor-memo card-editor-memo-back"
            value={backMemoContent}
            onChange={(e) => setBackMemoContent(e.target.value)}
            placeholder="Teaching note or grammar explanation (optional)"
            disabled={isSubmitting || isLevelingUp}
          />
        </div>

        <div className="context-dialog-row context-dialog-row-after">
          <input
            type="text"
            id="context-after-content"
            className="form-input context-dialog-input"
            aria-label="Context - After"
            value={contextAfterContent}
            onChange={(e) => setContextAfterContent(e.target.value)}
            placeholder="Answer context or follow-up sentence (optional)"
            disabled={isSubmitting || isLevelingUp}
          />
        </div>
          </>
        )}

        {false && (
          <div>
            <div>
              <div>
              <button type="button">
                  ★
                </button>
            </div>
            </div>
          </div>
        )}

        {/* Submit error message */}
        {submitError && (
          <ErrorMessage 
            error={submitError}
            onRetry={handleSubmit}
            onDismiss={() => setSubmitError(null)}
            type={submitError?.isNetworkError ? 'network' : 'api'}
          />
        )}

        {/* Form actions */}
        <div className="form-actions">
          {initialData && onDelete && (
            <button
              type="button"
              className="btn btn-delete"
              onClick={onDelete}
              disabled={isSubmitting || isLevelingUp || isEditDirty}
              title={isEditDirty ? 'Save or cancel changes before deleting.' : undefined}
            >
              Delete Flashcard
            </button>
          )}
          {canLevelUp && (
            <button
              type="button"
              className="btn btn-level-up-form"
              onClick={handleLevelUp}
              disabled={isSubmitting || isLevelingUp}
            >
              <ChevronsUp size={16} aria-hidden="true" />
              <span>{isLevelingUp ? 'Leveling...' : 'Level Up'}</span>
            </button>
          )}
          {showCancelAction && (
            <button
              type="button"
              className="btn btn-cancel"
              onClick={handleCancel}
              disabled={isSubmitting || isLevelingUp}
            >
              Cancel
            </button>
          )}
          {showSubmitAction && (
            <button
              type="submit"
              className="btn btn-submit"
              disabled={isSubmitting || isLevelingUp}
            >
              {isSubmitting ? 'Saving...' : (initialData ? 'Update' : 'Create')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default FlashCardForm;
